<template>
  <div class="calculator-points-miles" style="max-width: 720px; margin: 0 auto;">
    <p class="label mb-1">Select conversion type</p>

    <div class="conversion-type d-flex justify-content-center mt-0-5 mb-4">
      <div class="input-wrap mr-1 mr-sm-3">
        <input type="radio" name="points-miles" id="points-to-miles" @input="onUpdateConversionType($event)" checked>
        <label for="points-to-miles">{{ content.points_to_miles.label }}</label>
      </div>
      <div class="input-wrap ml-1 ml-sm-3">
        <input type="radio" name="points-miles" id="miles-to-points" @input="onUpdateConversionType($event)">
        <label for="miles-to-points">{{ content.miles_to_points.label }}</label>
      </div>
    </div>

    <div class="d-flex justify-content-center">
      <p class="fz-md mx-1 mx-sm-0" style="max-width: 585px;" v-html="bodyCopy"></p>
    </div>

    <div class="calculator mt-4">
      <div class="row-1">
        <div class="input-wrap marriott-points left-side w-100 mb-1 mb-md-0">
          <label for="value-1" class="label">{{ content[selectedConversion].inputLabel }}</label>
          <input v-if="selectedConversion === 'points_to_miles'" type="text" id="value-1" class="bold fz-26" v-model="userInputPTM" @input="onInput($event)" ref="textInputEl">
          <input v-else-if="selectedConversion === 'miles_to_points'" type="text" id="value-1" class="bold fz-26" v-model="userInputMTP" @input="onInput($event)" ref="textInputEl">
        </div>

        <p :class="`equals mx-1 mb-1`">&equals;</p>

        <div v-if="showBonus" class="row-3 d-flex justify-content-center align-items-end fz-sm mt-md-1 mb-md-1 mr-1">
          <div class="input-wrap input-bonus skywards-miles left-side w-100">
            <p class="d-flex align-items-center bold fz-26 w-100" v-html="calculatedSubTotal"></p>
          </div>

          <div class="bonus d-flex align-items-center ml-1 mb-0">
            <p class="plus mb-0">+</p>
            <p class="bonus-amount lh-1 ml-1">
              <span class="fz-26 bold">{{ numberWithCommas(bonus) }}</span>
              <br class="d-none d-md-inline"><span class="no-wrap fz-2xs"> Bonus Miles</span>
            </p>
          </div>

          <p class="equals mx-1">&equals;</p>
        </div>

        <div class="input-wrap skywards-miles left-side w-100 mt-1 mt-md-0">
          <p class="label">{{ content[selectedConversion].totalLabel }}</p>
          <p id="value-2" class="total d-flex align-items-center bg-bonvoy-white bold fz-26 w-100" v-html="calculatedTotal"></p>
        </div>
      </div>
    </div>

    <p class="text-center fz-3xs mt-4" v-html="disclaimerCopy"></p>

    <a role="button" :href="content[selectedConversion].cta_href" class="link-arrow mt-4 custom_click_track" :custom_click_track_value="content[selectedConversion].click_track_value" target="_blank">Transfer Now</a>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { numberWithCommas } from '../../utilities';

const content = APP_content.landing.calculator;
const selectedConversion = ref('points_to_miles');
const userInputPTM = ref(null);
const userInputMTP = ref(null);
const bonus = ref(0);
const bodyCopy = ref('');
const disclaimerCopy = ref('');
const MAX_POINTS = 240000;
const MAX_MILES = 100000;

onMounted(() => {
  bodyCopy.value = content[selectedConversion.value].body;
  disclaimerCopy.value = content[selectedConversion.value].disclaimer;
});

function getTotal(input, includeBonus) {
  let total = Math.round(input / content[selectedConversion.value].ratio);
  if (showBonus.value && includeBonus) {
    total += bonus.value;
  }
  return numberWithCommas(total);
}

function getTotalBasedOnConversionType(includeBonus = false) {
  if (selectedConversion.value === 'points_to_miles') {
    return getTotal(userInputPTM.value, includeBonus);
  } else { // miles_to_points
    return getTotal(userInputMTP.value, includeBonus);
  }
}

const calculatedSubTotal = computed(() => {
  return getTotalBasedOnConversionType();
});

const calculatedTotal = computed(() => {
  return getTotalBasedOnConversionType(true);
});

const showBonus = computed(() => {
  if (selectedConversion.value === 'points_to_miles') {
    if (userInputPTM.value >= 60000 && userInputPTM.value < 120000) {
      bonus.value = 5000;
    } else if (userInputPTM.value >= 120000 && userInputPTM.value < 180000) {
      bonus.value = 10000;
    } else if (userInputPTM.value >= 180000 && userInputPTM.value < 240000) {
      bonus.value = 15000;
    } else if (userInputPTM.value >= 240000) {
      bonus.value = 20000;
    }
    return userInputPTM.value >= 60000;
  }
});

function updateConversionType(conversion) {
  selectedConversion.value = conversion;
}

function onUpdateConversionType() {
  if (selectedConversion.value === 'points_to_miles') {
    updateConversionType('miles_to_points');
    bodyCopy.value = content.miles_to_points.body;
    disclaimerCopy.value = content.miles_to_points.disclaimer;
  } else { // miles_to_points
    updateConversionType('points_to_miles');
    bodyCopy.value = content.points_to_miles.body;
    disclaimerCopy.value = content.points_to_miles.disclaimer;
  }
}

// Keeps user from entering non-number characters by stripping them out
function stripNonNumbers(input) {
  return input.toString().replace(/\D+/, '');
}

function onInput(e) {
  if (selectedConversion.value === 'points_to_miles') {
    if (userInputPTM.value > MAX_POINTS) userInputPTM.value = MAX_POINTS;
    userInputPTM.value = stripNonNumbers(userInputPTM.value);
  } else { // miles_to_points

    if (userInputMTP.value > MAX_MILES) userInputMTP.value = MAX_MILES;
    userInputMTP.value = stripNonNumbers(userInputMTP.value);
  }
}
</script>
